import React, { useEffect } from 'react'
import styled from 'styled-components'
import ReactHtmlParser from 'react-html-parser'
import { lighten, rgba } from 'polished'
import { theme, UIColorThemes } from 'global'
import { IProduct } from 'models'
import { Flex, Heading, Box, Button, Text } from 'components'
import { useInView } from 'react-intersection-observer'
import { trackingProductView, trackingProductClick } from 'api/tracking'

const ProductContainer = styled(Flex)`
  cursor: pointer;
  margin: 8px;
  padding: 8px;
  border: 1px solid #e3e3e3;
  border-radius: 5px;
  max-width: 375px;
  height: 100%;
  text-align: center;
`

const ProductImage = styled.img`
  max-height: 200px;
  min-height: 200px;
  width: 100%;
  object-fit: cover;

  @media screen and (min-width: ${theme.maxWidths.large}) {
    max-height: 220px;
  }
`

const ProductLink = styled.a`
  color: ${theme.colors.linkPurple};
  text-transform: uppercase;
  font-weight: bold;
  margin: 8px 0;
`

const ProductPriceTag = styled(Box)`
  color: white;
  background: ${lighten(0.15, rgba(theme.colors.linkPurple, 0.75))};
  font-size: 1.1em;
  border: 1px solid ${theme.colors.linkPurple};
  padding: 6px 12px;
  top: 8px;
  right: 8px;
  border-radius: 25px;
  min-width: 70px;
  text-align: center;
  font-weight: bold;
`

const SubProduct = styled(Text)`
  width: 100%;
  font-weight: 600;
  font-size: 1.1em;
  margin: 2px 0;
  text-align: center;
`

interface IProductProps {
  product: IProduct
  animalColor: string
  animalUiTheme: UIColorThemes
}

export const Product = ({
  product,
  animalColor,
  animalUiTheme,
}: IProductProps) => {
  const [ref, inView] = useInView({
    threshold: 0.25,
  })

  const handleProductClick = () => {
    window.open(product.externalLink.url, '_newtab')
    trackingProductClick(product.title)
  }

  useEffect(() => {
    if (inView) {
      trackingProductView(product.title)
    }
    // eslint-disable-next-line
  }, [inView])

  return (
    <ProductContainer
      flexDirection='column'
      marginTop={3}
      onClick={handleProductClick}
      ref={ref}
    >
      <Flex>
        <ProductImage src={product.picture} />
        <ProductPriceTag position='absolute'>
          ${product.price.amount.toFixed(2)}
        </ProductPriceTag>
      </Flex>
      <Flex flexDirection='column' padding={2} flex='1'>
        {product.externalLink.domain && (
          <ProductLink>{product.externalLink.domain}</ProductLink>
        )}
        <Heading as='h2' marginTop={2} marginBottom={1}>
          {product.title}
        </Heading>
        <Flex
          height='100%'
          flexDirection='column'
          justifyContent='space-between'
        >
          {product.description && (
            <Flex flexDirection='column' padding={2}>
              {ReactHtmlParser(product.description)}
            </Flex>
          )}
          {product.subProducts && (
            <Box marginBottom={2}>
              {product.subProducts.map((sp, i) => (
                <Flex key={`${sp}${i}`}>
                  <Flex width={1}>
                    <SubProduct>{sp}</SubProduct>
                  </Flex>
                </Flex>
              ))}
            </Box>
          )}
          <Box
            width={1}
            padding='0 25px'
            marginTop={[2, 2, 2, 'auto']}
            marginBottom={[0, 0, 0, 2]}
          >
            <Button
              thin
              width={1}
              backgroundColor={animalColor}
              uiColorTheme={animalUiTheme}
            >
              {product.callToAction || 'Buy'}
            </Button>
          </Box>
        </Flex>
      </Flex>
    </ProductContainer>
  )
}
